import { useEffect, useState } from 'react';
import { getCart, onCartUpdated, removeCartLine, updateCartLine, clearLocalCart, isShopifyConfigured, type Cart } from '../../lib/shopify';

export default function CartIndicator() {
  const [cart, setCart] = useState<Cart | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const reload = async () => {
    if (!isShopifyConfigured()) return;
    setLoading(true);
    try {
      const c = await getCart();
      setCart(c);
    } catch (e) {
      console.error('[cart] reload failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    const off = onCartUpdated(reload);
    return off;
  }, []);

  const handleRemove = async (lineId: string) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const c = await removeCartLine(lineId);
      if (c) setCart(c);
      else setErrorMsg('削除に失敗しました');
    } catch (e) {
      console.error('[cart] remove failed:', e);
      setErrorMsg('削除中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleQty = async (lineId: string, qty: number) => {
    if (qty < 1) return;
    // 楽観的更新: クリック直後にUIを更新（API応答を待たずに見た目を変える）
    setCart(prev => prev ? {
      ...prev,
      lines: prev.lines.map(l => l.id === lineId ? { ...l, quantity: qty } : l),
      totalQuantity: prev.lines.reduce((sum, l) => sum + (l.id === lineId ? qty : l.quantity), 0),
    } : prev);
    setLoading(true);
    setErrorMsg(null);
    try {
      const c = await updateCartLine(lineId, qty);
      if (c) {
        setCart(c);  // サーバーからの正確な値で上書き
      } else {
        setErrorMsg('数量の更新に失敗しました');
        await reload();  // 失敗時は再取得で正しい状態に戻す
      }
    } catch (e) {
      console.error('[cart] qty update failed:', e);
      setErrorMsg('数量更新中にエラーが発生しました');
      await reload();
    } finally {
      setLoading(false);
    }
  };

  if (!isShopifyConfigured()) return null;
  // カートが空でもボタンは表示する (中身を確認できるように)
  const hasItems = cart && cart.totalQuantity > 0;
  const total = cart ? Number(cart.cost.totalAmount.amount) : 0;
  const currency = cart?.cost.totalAmount.currencyCode === 'JPY' ? '¥' : (cart?.cost.totalAmount.currencyCode || '¥');

  return (
    <>
      {/* フローティングカートボタン */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 bg-zinc-900 text-white rounded-full shadow-2xl hover:bg-zinc-800 transition-all flex items-center gap-2 px-5 py-3 cursor-pointer"
        aria-label="カートを開く"
      >
        <div className="relative">
          <i className="ri-shopping-cart-2-line text-xl"></i>
          {hasItems && (
            <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {cart!.totalQuantity}
            </span>
          )}
        </div>
        <span className="text-sm font-medium tracking-wider hidden sm:inline">カート</span>
      </button>

      {/* ドロワー */}
      {open && (
        <div className="fixed inset-0 z-50 flex">
          {/* 背景オーバーレイ */}
          <div
            className="flex-1 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-label="閉じる"
          />
          {/* パネル */}
          <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div className="flex items-center gap-2">
                <i className="ri-shopping-cart-2-line text-lg"></i>
                <h2 className="text-base font-bold tracking-wider">カート（{cart?.totalQuantity ?? 0}点）</h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-9 h-9 flex items-center justify-center text-zinc-500 hover:text-zinc-900 cursor-pointer"
                aria-label="閉じる"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {(() => {
                // quantity=0 のラインは在庫不足等で無効状態なので、警告を出す
                const visibleLines = cart?.lines.filter(l => l.quantity > 0) ?? [];
                const invalidLines = cart?.lines.filter(l => l.quantity === 0) ?? [];
                return (
                  <>
                    {invalidLines.length > 0 && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        <i className="ri-error-warning-line mr-1"></i>
                        在庫不足のため購入できないラインが {invalidLines.length} 件あります。下の「カートを空にする」で破棄してください。
                      </p>
                    )}
                    {visibleLines.length === 0 && invalidLines.length === 0 && (
                      <p className="text-center text-zinc-400 text-sm py-10">カートは空です</p>
                    )}
                  </>
                );
              })()}
              {(cart?.lines.filter(l => l.quantity > 0) ?? []).map((line) => {
                  // カート行の独自attributes (商品追加時にこちらで埋めた "_image_url" / "_option_label")
                  const attrMap = new Map((line.attributes ?? []).map(a => [a.key, a.value]));
                  const customImage = attrMap.get('_image_url');
                  const customLabel = attrMap.get('_option_label');
                  // 画像優先順: 1) サイトから付与した独自URL 2) Shopify variant.image 3) product.featuredImage
                  const displayImage = customImage
                    || line.merchandise.image?.url
                    || line.merchandise.product.featuredImage?.url
                    || '';
                  // ラベル優先順: 1) 独自ラベル (例: "ベージュ") 2) Shopify variant.title (Default Title/コード形式は非表示)
                  let displayLabel = customLabel || '';
                  if (!displayLabel) {
                    const t = (line.merchandise.title || '').trim();
                    const isDefault = !t || t === 'Default Title';
                    const isPulldownCode = /^[A-Z]\d+(-[A-Z]\d+)*$/.test(t);
                    if (!isDefault && !isPulldownCode) displayLabel = t;
                  }
                  return (
                  <div key={line.id} className="flex gap-3 pb-4 border-b border-zinc-100 last:border-b-0">
                    {displayImage ? (
                      <img
                        src={displayImage}
                        alt={line.merchandise.product.title}
                        className="w-16 h-16 object-cover rounded border border-zinc-100"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-zinc-100 rounded border border-zinc-100 flex items-center justify-center text-zinc-300">
                        <i className="ri-image-line"></i>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-zinc-800 leading-snug line-clamp-2">
                        {line.merchandise.product.title}
                      </p>
                      {displayLabel && (
                        <p className="text-[11px] text-zinc-500 mt-0.5">{displayLabel}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-zinc-300 rounded">
                          <button
                            type="button"
                            disabled={loading || line.quantity <= 1}
                            onClick={() => handleQty(line.id, line.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed text-sm cursor-pointer"
                            aria-label="減らす"
                          >
                            −
                          </button>
                          <span className="w-8 text-center text-sm">{line.quantity}</span>
                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => handleQty(line.id, line.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed text-sm cursor-pointer"
                            aria-label="増やす"
                          >
                            +
                          </button>
                        </div>
                        <p className="text-sm font-bold text-zinc-900">
                          {currency}{Number(line.cost.totalAmount.amount).toLocaleString()}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={loading}
                        onClick={() => handleRemove(line.id)}
                        className="mt-1 text-[11px] text-zinc-400 hover:text-red-500 transition-colors cursor-pointer disabled:cursor-not-allowed"
                      >
                        削除
                      </button>
                    </div>
                  </div>
                  );
                })}
            </div>

            <div className="border-t border-zinc-100 px-5 py-4 space-y-3 bg-zinc-50">
              {errorMsg && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 flex items-start gap-1.5">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span>{errorMsg}</span>
                </p>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">小計</span>
                <span className="text-lg font-bold">
                  {currency}{total.toLocaleString()}
                </span>
              </div>
              <p className="text-[11px] text-zinc-500">送料・税金はお支払い画面で計算されます</p>
              {hasItems && cart?.checkoutUrl ? (
                <a
                  href={cart.checkoutUrl}
                  className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center py-3.5 rounded-lg font-bold transition-colors"
                >
                  <i className="ri-bank-card-line mr-1"></i>
                  お支払いへ進む
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="block w-full bg-zinc-300 text-white text-center py-3.5 rounded-lg font-bold cursor-not-allowed"
                >
                  カートに商品がありません
                </button>
              )}
              {/* デバッグ/復旧用: カートを完全に破棄して新規からやり直す */}
              {cart && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('現在のカートを完全に破棄します。よろしいですか？')) {
                      clearLocalCart();
                      setCart(null);
                      setErrorMsg(null);
                    }
                  }}
                  className="block w-full text-center py-1 text-[10px] text-zinc-400 hover:text-red-500 cursor-pointer"
                >
                  カートを空にする（やり直す）
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="block w-full text-center py-2 text-xs text-zinc-500 hover:text-zinc-800 cursor-pointer"
              >
                買い物を続ける
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
